import { useMasters } from '../context/MastersContext';
import { CostCenterDisplayContext, generateCostCenterDisplayName } from '../types/filterTypes';

/**
 * Hook to provide cost center options with contextual display names
 * Solves the "Aravu (STL)" vs "Aravu (DV)" confusion by adding unit context when needed
 */
export function useCostCenterDisplay() {
  const { costCenters, units } = useMasters();

  const getCostCenterWithContext = (
    costCenterId: string,
    unitId?: string,
    showUnitContext: boolean = false
  ): CostCenterDisplayContext | null => {
    const costCenter = costCenters.find(cc => cc.id === costCenterId);
    if (!costCenter) return null;

    const unit = unitId ? units.find(u => u.id === unitId) : undefined;
    
    return {
      costCenterId: costCenter.id,
      costCenterName: costCenter.name,
      unitId: unit?.id,
      unitName: unit?.name,
      typeName: costCenter.type,
      displayName: generateCostCenterDisplayName(
        costCenter,
        unit,
        costCenters,
        showUnitContext
      ),
    };
  };

  const getCostCenterOptionsForType = (
    typeId: string,
    selectedUnitId?: string,
    showUnitContext: boolean = true
  ) => {
    return costCenters
      .filter(cc => cc.type === typeId)
      .map(cc => {
        const unit = selectedUnitId ? units.find(u => u.id === selectedUnitId) : undefined;
        const displayName = generateCostCenterDisplayName(cc, unit, costCenters, showUnitContext);
        
        return {
          value: cc.id,
          label: displayName,
          unitId: selectedUnitId,
          originalName: cc.name,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  const hasDuplicateCostCenterNames = (typeId: string): boolean => {
    const ccInType = costCenters.filter(cc => cc.type === typeId);
    const nameCount = ccInType.reduce((acc, cc) => {
      acc[cc.name] = (acc[cc.name] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    return Object.values(nameCount).some(count => (count as number) > 1);
  };

  return {
    getCostCenterWithContext,
    getCostCenterOptionsForType,
    hasDuplicateCostCenterNames,
  };
}
